<template>
  <div v-if="userRole !== null" class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <div class="user-data-container">
              <div class="user-name">
                {{ isMobile ? activeAccountLabel[0] : activeAccountLabel }}
              </div>
              <div
                class="user-role"
                :role="activeAccountRole"
                :extended="userRole.role === UserRoles.EXTERNO && userRole.externalSpec == ExternalSpec.ARMADOR"
              >
                {{ isMobile ? activeAccountRole[0] : activeAccountRole }}
              </div>
              <div
                v-if="
                  userRole.role === UserRoles.EXTERNO &&
                  userRole.externalSpec == ExternalSpec.ARMADOR &&
                  !activeArmadorId
                "
                class="user-role-spec"
                :weste="userRole.armadorType == ArmadorType.WESTE"
              >
                {{ userRole.armadorType == ArmadorType.WESTE ? 'W' : 'E' }}
              </div>
            </div>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <!-- Account switcher -->
        <div v-if="availableAccounts.length > 1" class="account-switcher">
          <header class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4 mb-1">
            Cambiar cuenta
          </header>
          <div
            v-for="account in availableAccounts"
            :key="account.id ?? 'personal'"
            class="account-item px-3 py-2 mx-1 rounded cursor-pointer"
            :class="{ 'account-item--active': account.id === activeArmadorId }"
            @click="switchAccount(account.id)"
          >
            <div class="account-item__avatar">
              {{ account.initials }}
            </div>
            <div class="account-item__info">
              <div class="account-item__name">{{ account.name }}</div>
              <div class="account-item__type">{{ account.type }}</div>
            </div>
            <VaIcon
              v-if="account.id === activeArmadorId"
              name="mso-check_circle"
              color="success"
              size="18px"
              class="ml-auto shrink-0"
            />
          </div>
          <VaListSeparator class="mx-3 my-2" />
        </div>

        <!-- Existing menu items -->
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ group.name }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="onItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ item.name }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, Ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import roleCapacitiesManager, {
  UserRoles,
  ExternalSpec,
  ArmadorType,
  UserRole,
  getRoleName,
} from '../../../../managers/identity/RoleCapacitiesManager'
import identityManager, { LoggedInUser } from '../../../../managers/identity/IdentityManager'
import dataStorageManager, { BucketDefaultIDs } from '../../../../managers/data/DataStorageManager'
import { IArmador } from '../../../../services/api-services/ArmadoresServices'
import {
  IRequest,
  RequestObjectType,
  RequestStatus,
  isApproved,
} from '../../../../services/api-services/RequestsServices'
import { IPermisoControl } from '../../../../services/api-services/RepresentantesServices'
import { IEmbarcacion } from '../../../../services/api-services/EmbarcacionesServices'
import { getEmbarcacionesAsignadas } from '../../../../services/utils/PermisosResolver'
import { rutify } from '../../../../services/apiUtils'

const currentUser: Ref<LoggedInUser | null> = ref(identityManager.getUser())
const userRole: Ref<UserRole | null> = roleCapacitiesManager.getRefUserRole()
const activeArmadorId: Ref<number | null> = identityManager.getRefActiveArmadorId()

const roleName = computed(() =>
  userRole.value ? getRoleName(userRole.value.role, userRole.value.externalSpec || undefined) : '',
)

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

// --- Account switcher ---

interface AccountOption {
  id: number | null
  name: string
  type: string
  initials: string
}

const representedArmadores = ref<IArmador[]>([])
// true si el usuario logueado es él mismo un armador (tiene entrada propia en ARMADORES)
const userIsOwnArmador = ref(false)

watch(
  userRole,
  (newVal) => {
    currentUser.value = identityManager.getUser()
    loadRepresentedArmadores()
  },
  { immediate: true },
)

async function loadRepresentedArmadores() {
  if (!currentUser.value) {
    representedArmadores.value = []
    userIsOwnArmador.value = false
    return
  }
  const armadores = dataStorageManager.getCurrentBucketData<IArmador>(BucketDefaultIDs.ARMADORES) || []
  userIsOwnArmador.value = armadores.some((a) => rutify(a.rut_representante) === rutify(currentUser.value!.rut))

  const userId = currentUser.value.id_usuario.toString()
  const solicitudes = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
  const myRepresentaciones = solicitudes.filter(
    (s) =>
      s.tipo_objeto === RequestObjectType.RepresentanteArmador &&
      s.id_objeto_2 === userId &&
      isApproved(s.estado) &&
      new Date(s.fecha_caducidad) > new Date(),
  )
  if (myRepresentaciones.length === 0) {
    representedArmadores.value = []
    return
  }

  // IDs de embarcaciones cuyo enrolamiento está rechazado (no deben contar como asignables)
  const embarcacionesList = dataStorageManager.getCurrentBucketData<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES) || []
  const embarcacionesRechazadas = new Set<number>()
  for (const emb of embarcacionesList) {
    const sol = solicitudes
      .filter((r) => r.tipo_objeto === RequestObjectType.Embarcacion && parseInt(r.id_objeto) === emb.id)
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
    if (sol?.estado === RequestStatus.REJECTED) {
      embarcacionesRechazadas.add(emb.id)
    }
  }

  // Embarcaciones asociadas vigentes por armador (excluyendo rechazadas)
  function embarcacionesActivasDelArmador(armadorId: number): number {
    return solicitudes
      .filter(
        (r) =>
          r.tipo_objeto === RequestObjectType.Asociacion_embarcacion &&
          r.id_objeto_2 === armadorId.toString() &&
          r.estado === RequestStatus.APPROVED,
      )
      .map((r) => parseInt(r.id_objeto))
      .filter((id) => {
        const emb = embarcacionesList.find((e) => e.id === id)
        return emb && emb.activo && !embarcacionesRechazadas.has(id)
      }).length
  }

  // Filtrar representaciones inválidas: en modo explícito (hay otros reps activos) sin embarcaciones asignadas
  const permisosList = dataStorageManager.getCurrentBucketData<IPermisoControl>(BucketDefaultIDs.PERMISOS) || []
  const validRepresentaciones = myRepresentaciones.filter((repSolicitud) => {
    const armadorId = parseInt(repSolicitud.id_objeto)
    // Si el armador no tiene embarcaciones activas no rechazadas, la representación no es válida
    if (embarcacionesActivasDelArmador(armadorId) === 0) return false
    // #7: Representante Legal → acceso espejado al armador (sin matriz por embarcación). Nunca debe
    // filtrarse por la regla de "embarcaciones asignadas": cuando coexiste con apoderados (modo
    // explícito) su matriz está vacía y quedaba excluido del selector de cuentas → no podía acceder
    // al armador desde el menú. Siempre es válido si el armador tiene embarcaciones activas.
    if (repSolicitud.estado === RequestStatus.APPROVED_LEGAL) return true
    const otrosRepsActivos = solicitudes.filter(
      (s) =>
        s.tipo_objeto === RequestObjectType.RepresentanteArmador &&
        s.id_objeto === repSolicitud.id_objeto &&
        s.id !== repSolicitud.id &&
        s.estado === RequestStatus.APPROVED &&
        new Date(s.fecha_caducidad) > new Date(),
    ).length
    if (otrosRepsActivos === 0) return true // Único rep → modo implícito → válido
    const permisosRep = permisosList.filter((p) => Number(p.representante) === Number(repSolicitud.id))
    const asignadas = getEmbarcacionesAsignadas(permisosRep).filter((id) => !embarcacionesRechazadas.has(id))
    return asignadas.length > 0
  })

  // Filtrar armadores que tengan enrolamiento aprobado y vigente
  const ahora = new Date()
  const armadorIds = validRepresentaciones
    .filter((repSolicitud) => {
      const armadorId = parseInt(repSolicitud.id_objeto)
      return solicitudes.some(
        (s) =>
          s.tipo_objeto === RequestObjectType.Armador &&
          parseInt(s.id_objeto) === armadorId &&
          s.estado === RequestStatus.APPROVED &&
          new Date(s.fecha_caducidad) > ahora,
      )
    })
    .map((s) => parseInt(s.id_objeto))
  representedArmadores.value = armadores.filter((a) => armadorIds.includes(a.id))
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

const availableAccounts = computed<AccountOption[]>(() => {
  if (!currentUser.value) return []
  const accounts: AccountOption[] = []

  // Mostrar cuenta propia si:
  // - no tiene representaciones, o
  // - el usuario es armador él mismo, o
  // - el usuario es representante puro (siempre debe poder volver a su perfil propio)
  // Se usa currentUser.value?.rol (rol base de sesión) en lugar del rol computado activo,
  // ya que al activar una cuenta de armador representado el rol computado cambia a EXTERNO.
  const hasRepresentations = representedArmadores.value.length > 0
  const isRepresentantePuro = currentUser.value?.rol === UserRoles.REPRESENTANTE
  if (!hasRepresentations || userIsOwnArmador.value || isRepresentantePuro) {
    accounts.push({
      id: null,
      name: currentUser.value.nombre_completo,
      type: getRoleName(currentUser.value.rol as UserRoles, userRole.value?.externalSpec ?? undefined),
      initials: getInitials(currentUser.value.nombre_completo),
    })
  }

  representedArmadores.value.forEach((armador) => {
    accounts.push({
      id: armador.id,
      name: armador.razon_social || armador.nombre_representante,
      type: 'Armador',
      initials: getInitials(armador.razon_social || armador.nombre_representante),
    })
  })
  return accounts
})

const activeAccount = computed(
  () => availableAccounts.value.find((a) => a.id === activeArmadorId.value) ?? availableAccounts.value[0],
)

const activeAccountLabel = computed(() => activeAccount.value?.name ?? currentUser.value?.nombre_completo ?? '')
const activeAccountRole = computed(() => activeAccount.value?.type ?? roleName.value)

const router = useRouter()

function switchAccount(accountId: number | null) {
  identityManager.setActiveArmadorId(accountId)
  isShown.value = false
  router.push({ name: 'dashboard' })
}

onMounted(() => {
  loadRepresentedArmadores()
  dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, loadRepresentedArmadores)
  dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, loadRepresentedArmadores)
  dataStorageManager.subscribeBucket(BucketDefaultIDs.PERMISOS, loadRepresentedArmadores)
})

// --- Existing ---

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    isMobile: boolean
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'Cuenta',
        separator: true,
        list: [
          {
            name: 'Perfil',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'Cerrar sesión',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const onItemClick = (item: ProfileListItem) => {
  if (item.name === 'Cerrar sesión') {
    router.push({ name: 'login' }).then(() => {
      identityManager.logOut()
    })
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}

.user-data-container {
  display: flex;
  flex-direction: row;
}

.user-name {
  padding: 5px 10px;
  background-color: #e6eef5;
  color: #0f0f0f;
  border-radius: 10px 0 0 10px;
}

.user-role {
  padding: 5px 10px;
  background-color: #224796;
  color: #f8f8f8;
  border-radius: 0 10px 10px 0;
}

.user-role[extended='true'] {
  border-radius: 0;
}

.user-role-spec {
  padding: 5px 10px;
  background-color: #566688;
  color: #f8f8f8;
  border-radius: 0 10px 10px 0;
}

.user-role-spec[weste='true'] {
  background-color: #4b7ad3;
}

// Account switcher
.account-switcher {
  padding-bottom: 0;
}

.account-item {
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.15s;

  &:hover {
    background: var(--hover-color);
  }

  &--active {
    background: color-mix(in srgb, #224796 8%, transparent);

    &:hover {
      background: color-mix(in srgb, #224796 14%, transparent);
    }
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #224796;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--va-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__type {
    font-size: 11px;
    color: var(--va-secondary);
    opacity: 0.85;
  }
}
</style>
